// Pure logic for the sign-up form.
//
// Lives outside the component so it can be tested without a DOM, matching
// prescriptionForm.js. The component keeps rendering and state; every decision
// about what is valid and what a failure means is here.

import { apiErrorMessage } from './prescriptionForm';

/**
 * The minimum password length.
 *
 * Mirrors `binding:"required,min=6"` on routes.RegisterUserRequest.Password.
 * This is a deliberate duplicate of a backend rule, kept for one reason: the
 * server's rejection is a gin validator string —
 *
 *     Key: 'RegisterUserRequest.Password' Error:Field validation for
 *     'Password' failed on the 'min' tag
 *
 * — which is not something to show a person. Checking here turns a round trip
 * and an unreadable error into inline feedback.
 *
 * It is named and colocated rather than written as a bare 6 in the component,
 * so the mirror is visible and greppable. If the Go constant moves, this is the
 * one place to follow it. (See docs/AUDIT-2026-07.md §4 on contract drift — the
 * units work removed the mirrors that could be removed; this one cannot be,
 * because the server has no endpoint that publishes it.)
 */
export const MIN_PASSWORD_LENGTH = 6;

/** Blank starting state for the sign-up form. */
export const EMPTY_REGISTRATION = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const asText = (value) => String(value ?? '').trim();

/**
 * Validate the sign-up form, returning field-keyed errors.
 *
 * Returns every problem at once rather than the first. The previous form
 * reported only the password mismatch, and only after a round trip for
 * everything else, so a user with two mistakes discovered them one at a time.
 *
 * @param {object} form
 * @returns {Object<string, string>} empty when the form is valid
 */
export function validateRegistrationForm(form = {}) {
  const errors = {};

  const username = asText(form.username);
  if (username === '') {
    errors.username = 'Choose a username.';
  }

  const email = asText(form.email);
  if (email === '') {
    errors.email = 'Enter your email address.';
  } else if (!isEmailish(email)) {
    // Deliberately permissive — see isEmailish. The server holds the real rule.
    errors.email = 'That does not look like an email address.';
  }

  // Not trimmed: leading and trailing spaces are legitimate password
  // characters, and silently stripping them would make the password the user
  // set differ from the one they typed.
  const password = String(form.password ?? '');
  if (password === '') {
    errors.password = 'Choose a password.';
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }

  const confirmPassword = String(form.confirmPassword ?? '');
  if (confirmPassword === '') {
    errors.confirmPassword = 'Repeat your password.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

/**
 * A deliberately loose email check: something, an @, something, a dot,
 * something.
 *
 * Full RFC 5322 validation in a client is a well-known way to reject valid
 * addresses — the server owns the real rule (`binding:"email"`). This only
 * catches the obvious typo before a round trip.
 */
export function isEmailish(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * The request body for a valid form.
 *
 * confirmPassword is a client-side concept and is not sent — the server has no
 * such field, and posting it would be noise on the wire.
 */
export function buildRegistrationRequest(form) {
  const errors = validateRegistrationForm(form);
  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  return {
    body: {
      username: asText(form.username),
      email: asText(form.email),
      password: String(form.password ?? ''),
    },
  };
}

/**
 * Map a failed sign-up to something the user can act on.
 *
 * Every failure used to become "Registration failed. Please try again.", so a
 * taken username — the single most likely reason to fail, and one the user can
 * fix in seconds — was indistinguishable from a server outage.
 *
 * A 409 now means one thing: the USERNAME is taken. Usernames are public on
 * every roster, so saying so leaks nothing, and the user cannot proceed
 * without being told. A taken EMAIL is never an error — the server answers it
 * exactly as it answers a free one (api-contract.md USER-5) — so nothing here
 * may suggest the address was the problem. That is the same reticence
 * signInErrorMessage keeps for a 401.
 */
export function signUpErrorMessage(error) {
  if (error?.response?.status === 409) {
    return apiErrorMessage(error, 'That username is taken. Choose another.');
  }
  if (!error?.response) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  // Registration is invite-only (api-contract USER-16). The server's refusal
  // is one fixed sentence whoever asks, so it is safe to relay; the added hint
  // is the one thing an invited person can get wrong — signing up under a
  // different address from the one that was invited. The fallback is explicit
  // because apiErrorMessage would otherwise show axios's "Request failed with
  // status code 403".
  if (error.response.status === 403) {
    const said = error.response.data?.error || 'Registration is by invitation only.';
    return `${said} If you were invited, sign up with the email address the invitation was for.`;
  }
  // Sign-up mounts only when the server can send mail. Unmounted, it is a 404,
  // and "something went wrong" would send the user round the form again.
  if (error.response.status === 404) {
    return 'Sign-up is not open on this server yet. Ask your coach for help getting started.';
  }
  // A 400 is a validation failure the client should have caught. Surfacing the
  // gin validator string verbatim would be unreadable, so it gets a plain
  // message rather than apiErrorMessage's passthrough.
  if (error.response.status === 400) {
    return 'Some of those details were not accepted. Check them and try again.';
  }
  return apiErrorMessage(error, 'Something went wrong creating your account.');
}
